import React, { useEffect, useState } from 'react';
import { Card, Table, Spin, Modal, Alert } from 'antd';
import { AlertOutlined, XOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import * as VThunk from '../../slices/vehicles/thunk';
import { useDispatch, useSelector } from 'react-redux';
import './LastestStatus.css';

const LastestStatus = () => {
  const dispatch = useDispatch();
  const { lastestStatusList } = useSelector(state => ({
    lastestStatusList: state.Vehicles.lastestStatusList || []
  }));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alarmModalVisible, setAlarmModalVisible] = useState(false);
  const [alarmData, setAlarmData] = useState([]);

  // 计算侵限信息
  const calculateLimitExceeded = (item) => {
    if (!item.is_limit_exceeded) {
      return { isExceeded: false, type: '', degree: '0.00' };
    }
    
    const relativeAngle = parseFloat(item.relative_angle) || 0;
    const absAngle = Math.abs(relativeAngle);
    
    if (relativeAngle > 0) {
      const rightLimit = parseFloat(item.right_limit_angle) || 0;
      const exceededDegree = (absAngle - rightLimit).toFixed(2);
      return { isExceeded: true, type: '右侵限', degree: exceededDegree };
    } else {
      const leftLimit = parseFloat(item.left_limit_angle) || 0;
      const exceededDegree = (absAngle - leftLimit).toFixed(2);
      return { isExceeded: true, type: '左侵限', degree: exceededDegree };
    }
  };

  // 将数据转换为表格格式
  const transformData = (data) => {
    return data.map(item => {
      const limitInfo = calculateLimitExceeded(item);
      return {
        key: item.id,
        vehicle_id: item.vehicle_id,
        licensePlate: item.vehicle_name,
        sn: item.sn,
        windSpeed: item.backup_1 ? (parseInt(item.backup_1) / 1000).toFixed(3) : '0.000',
        moment: item.backup_2 ? (parseInt(item.backup_2) / 1000).toFixed(3) : '0.000',
        weight: item.backup_3 ? (parseInt(item.backup_3) / 1000).toFixed(3) : '0.000',
        boomLength: item.backup_4 ? (parseInt(item.backup_4) / 1000).toFixed(3) : '0.000',
        angle: item.backup_5 ? (parseInt(item.backup_5) / 1000).toFixed(3) : '0.000',
        rotateAngle: item.backup_6 ? (parseInt(item.backup_6) / 1000).toFixed(3) : '0.000',
        isLimitExceeded: limitInfo.isExceeded,
        limitType: limitInfo.type,
        exceededDegree: limitInfo.degree,
      };
    });
  };

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      await dispatch(VThunk.getLastestStatus());
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    fetchData();
  }, [dispatch]);

  // 定时刷新
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // 更新表格和报警
  useEffect(() => {
    const transformedData = transformData(lastestStatusList);
    setTableData(transformedData);
    
    const exceededVehicles = transformedData.filter(item => item.isLimitExceeded);
    if (exceededVehicles.length > 0) {
      setAlarmData(exceededVehicles);
      setAlarmModalVisible(true);
    }
  }, [lastestStatusList]);

  // 关闭报警
  const handleCloseAlarm = () => {
    setAlarmModalVisible(false);
  };

  // 表格列定义
  const columns = [
    {
      title: '车牌号',
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      width: 200,
      align: 'center',
      render: (text, record) => <Link to={`/vehicles/${record.vehicle_id}?tab=location`}>{text}</Link>,
    },
    { title: '风速', dataIndex: 'windSpeed', key: 'windSpeed', width: 80, align: 'center' },
    { title: '力矩', dataIndex: 'moment', key: 'moment', width: 100, align: 'center' },
    { title: '重量', dataIndex: 'weight', key: 'weight', width: 80, align: 'center' },
    { title: '臂长', dataIndex: 'boomLength', key: 'boomLength', width: 80, align: 'center' },
    { title: '角度', dataIndex: 'angle', key: 'angle', width: 80, align: 'center' },
    { title: '旋转角度', dataIndex: 'rotateAngle', key: 'rotateAngle', width: 80, align: 'center' },
  ];

  return (
    <>
      <Card title="车辆车身状态实时数据" className="compact-card" style={{ height: '22em' }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={tableData}
            size="small"
            pagination={{ pageSize: 3, size: 'small' }}
            scroll={{ x: true }}
            className="compact-table"
          />
        </Spin>
      </Card>

      {/* 报警弹窗 */}
      <Modal
        open={alarmModalVisible}
        onCancel={handleCloseAlarm}
        footer={null}
        closable={false}
        width={480}
        wrapClassName="alarm-modal-wrap"
      >
        <div className="alarm-modal-content">
          <div className="alarm-header">
            <div className="alarm-icon-wrapper">
              <div className="alarm-icon">
                <AlertOutlined />
              </div>
              <div>
                <h2 className="alarm-title">侵限报警</h2>
                <p className="alarm-subtitle">检测到车辆侵限，请立即处理</p>
              </div>
            </div>
            <button className="alarm-close-btn" onClick={handleCloseAlarm}>
              <XOutlined />
            </button>
          </div>
          
          <div className="alarm-list">
            {alarmData.map((item, index) => (
              <Alert
                key={index}
                message={
                  <div>
                    <div className="alarm-item-plate">车牌: {item.licensePlate}</div>
                    <div className="alarm-item-info">{item.limitType} - 侵限 {Math.abs(parseFloat(item.exceededDegree)).toFixed(2)}°</div>
                  </div>
                }
                type="error"
                showIcon={false}
                className="alarm-item"
              />
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LastestStatus;